import React, { useMemo } from "react";
import { usePersistedState } from "../../hooks/utility/usePersistedState";
import { useNavigate } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { legendIsolateClickPie, legendIsolateClickDataset } from "../../Utils/chartLegend";
import { useGetEauKpis } from "../../hooks/analytics/useGetKpisAnalytics";
import { useGetEauRatios } from "../../hooks/analytics/useGetEauRatios";
import { useGetEauAnalytics } from "../../hooks/analytics/useGetModuleAnalytics";
import { useGetPerGroupAnalytics } from "../../hooks/analytics/useGetPerGroupAnalytics";
import { useGetPosteChampsMetier } from "../../hooks/metadata/useGetPosteChampsMetier";
import { useGetSites } from "../../hooks/site/useGetSites";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import { useGetActivites } from "../../hooks/reference/useGetActivites";
import { useGetFilieres } from "../../hooks/reference/useGetFilieres";
import { useGetRegions } from "../../hooks/reference/useGetRegions";
import { usePageTitle } from "../../hooks/utility/usePageTitle";
import Topbar from "Components/Topbar/Topbar";
import { MultiSearchableCombobox } from "Components/MultiSearchableCombobox";
import {
  Droplets,
  TrendingUp,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Ratio,
} from "lucide-react";
import type { User as BackendUser } from "../../types/user";
import "./WaterPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

interface WaterPageProps {
  user: BackendUser;
}

const ACCENT = "#3b82f6";

// Format large numbers like in Def_Eau: 35.7M, 5.2k, etc.
function formatLargeNumber(num: number): string {
  const absNum = Math.abs(num);
  let formatted: string;
  if (absNum >= 1_000_000) {
    formatted = `${(absNum / 1_000_000).toFixed(1)}M`;
  } else if (absNum >= 1_000) {
    formatted = `${(absNum / 1_000).toFixed(1)}k`;
  } else {
    formatted = absNum.toFixed(1);
  }
  return num < 0 ? `-${formatted}` : formatted;
}


const CHART_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#06b6d4",
  "#84cc16",
  "#6366f1",
  "#e11d48",
];

// Color maps matching the Python definitions (Def_Eau)
const FAMILLE_COLORS: Record<string, string> = {
  AGRUMES: "#ea9f7a",
  AFOURER: "#ea9f7a",
  CLEMENTINE: "#ecc55f",
  ORANGE: "#cf9168",
  "AUTRES AGRUMES": "#E8EC53",
  "AUTRES FRUITIERS": "#699511",
  "AUTRES ARBO": "#699511",
  AVOCAT: "#067800",
  OLIVIER: "#2C4D13",
  CEREALES: "#7a7b53",
  "FRUITS ROUGES": "#8924ad",
  MARAICHAGE: "#abc340",
  "PLANTES AROMATIQUES": "#086e00",
};

function getGroupColor(
  name: string,
  colorMap: Record<string, string>,
  fallbackIndex: number
): string {
  const upper = name.toUpperCase().trim();
  if (colorMap[upper]) return colorMap[upper];
  for (const [key, color] of Object.entries(colorMap)) {
    if (upper.includes(key) || key.includes(upper)) return color;
  }
  return CHART_COLORS[fallbackIndex % CHART_COLORS.length];
}

const WaterPage: React.FC<WaterPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const pageTitle = usePageTitle();
  const handleLogout = () => navigate("/");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  // Determine current campaign (Oct–Sep)
  const currentCampaign = currentMonth >= 10 ? `${currentYear}/${(currentYear + 1) % 100}` : `${currentYear - 1}/${currentYear % 100}`;

  // Shared filter keys between Energy and Water pages for connected filtering
  const [filterType, setFilterType] = usePersistedState<"months" | "campaigns">("shared-filterType", "months");
  const [fromPeriod, setFromPeriod] = usePersistedState("shared-fromPeriod", `${currentYear}-01`);
  const [toPeriod, setToPeriod] = usePersistedState("shared-toPeriod", `${currentYear}-12`);
  const [fromCampaign, setFromCampaign] = usePersistedState("shared-fromCampaign", currentCampaign);
  const [toCampaign, setToCampaign] = usePersistedState("shared-toCampaign", currentCampaign);

  // Helper: Convert campaign (e.g., "23/24") to date range
  const campaignToMonths = (campaign: string): { start: string; end: string } => {
    const [startYear, endYear] = campaign.split('/').map(y => 2000 + parseInt(y));
    return {
      start: `${startYear}-10`, // October of start year
      end: `${endYear}-09`,     // September of end year
    };
  };

  // Calculate effective periods based on filter type
  const effectivePeriods = useMemo(() => {
    if (filterType === "campaigns") {
      const fromRange = campaignToMonths(fromCampaign);
      const toRange = campaignToMonths(toCampaign);
      return {
        from_period: fromRange.start,
        to_period: toRange.end,
      };
    }
    return {
      from_period: fromPeriod,
      to_period: toPeriod,
    };
  }, [filterType, fromPeriod, toPeriod, fromCampaign, toCampaign]);

  // Generate list of available campaigns (last 10 years)
  const availableCampaigns = useMemo(() => {
    const campaigns: string[] = [];
    const startYear = currentYear - 5;
    const endYear = currentYear + 5;
    for (let year = startYear; year <= endYear; year++) {
      campaigns.push(`${year % 100}/${(year + 1) % 100}`);
    }
    return campaigns;
  }, [currentYear]);

  // Generate list of available months (5 years back and forward)
  const availableMonths = useMemo(() => {
    const months: { value: string; label: string }[] = [];
    const monthNames = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];
    const startYear = currentYear - 5;
    const endYear = currentYear + 5;
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const value = `${year}-${String(month).padStart(2, '0')}`;
        const label = `${monthNames[month - 1]} ${year}`;
        months.push({ value, label });
      }
    }
    return months;
  }, [currentYear]);

  const [dimensionType, setDimensionType] = usePersistedState<"produit" | "famille">(
    "water-dimensionType",
    "famille"
  );
  const [selectedSites, setSelectedSites] = usePersistedState<(number | string)[]>(
    "shared-sites",
    []
  );
  const [selectedBUs, setSelectedBUs] = usePersistedState<(number | string)[]>("shared-bus", []);
  const [selectedActivites, setSelectedActivites] = usePersistedState<(number | string)[]>(
    "shared-activites",
    []
  );
  const [selectedFilieres, setSelectedFilieres] = usePersistedState<(number | string)[]>(
    "shared-filieres",
    []
  );
  const [selectedRegions, setSelectedRegions] = usePersistedState<(number | string)[]>(
    "shared-regions",
    []
  );

  const [activeTab, setActiveTab] = usePersistedState<"kpis" | "ratios">("water-tab", "kpis");

  const { data: sites = [] } = useGetSites();
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();

  // Get champs metier for eau module (Poste 2 = EAU)
  const { data: champsMetier = [] } = useGetPosteChampsMetier({ posteId: 2 });

  // Auto-detect champ metier IDs for water sources
  const champSurfaceId = useMemo(
    () => champsMetier.find((c) => c.code === "EAU_Surface_m3")?.id ?? null,
    [champsMetier]
  );
  const champSouterraineId = useMemo(
    () => champsMetier.find((c) => c.code === "EAU_Souterraine_m3")?.id ?? null,
    [champsMetier]
  );
  const champReseauId = useMemo(
    () => champsMetier.find((c) => c.code === "EAU_Réseau_m3")?.id ?? null,
    [champsMetier]
  );
  const champTraiteeId = useMemo(
    () => champsMetier.find((c) => c.code === "EAU_Traitée_m3")?.id ?? null,
    [champsMetier]
  );

  // Common filter values for analytics calls - convert to number arrays
  const siteIds = selectedSites.length > 0 ? selectedSites.map(id => Number(id)) : undefined;
  const buIds = selectedBUs.length > 0 ? selectedBUs.map(id => Number(id)) : undefined;
  const activiteIds = selectedActivites.length > 0 ? selectedActivites.map(id => Number(id)) : undefined;
  const filiereIds = selectedFilieres.length > 0 ? selectedFilieres.map(id => Number(id)) : undefined;
  const regionIds = selectedRegions.length > 0 ? selectedRegions.map(id => Number(id)) : undefined;

  // BU IDs and names for per-BU water KPIs
  const perBUIds = useMemo(
    () => (selectedBUs.length > 0 ? selectedBUs.map(Number) : bus.map((b) => b.id)),
    [selectedBUs, bus]
  );

  const buNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    for (const b of bus) map[b.id] = b.name;
    return map;
  }, [bus]);

  const baseWaterGroupFilters = useMemo(
    () => ({
      from_period: effectivePeriods.from_period,
      to_period: effectivePeriods.to_period,
      ...(siteIds ? { site_ids: siteIds } : {}),
      ...(activiteIds ? { activite_ids: activiteIds } : {}),
      ...(filiereIds ? { filiere_ids: filiereIds } : {}),
      ...(regionIds ? { region_ids: regionIds } : {}),
    }),
    [effectivePeriods, siteIds, activiteIds, filiereIds, regionIds]
  );

  const { groupData: buSurfaceData, isLoading: buSurfaceLoading } = useGetPerGroupAnalytics({
    baseFilters: { ...baseWaterGroupFilters, champ_metier: champSurfaceId },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "eau",
    enabled: !!champSurfaceId && perBUIds.length > 0,
  });

  const { groupData: buSouterraineData, isLoading: buSouterraineLoading } = useGetPerGroupAnalytics({
    baseFilters: { ...baseWaterGroupFilters, champ_metier: champSouterraineId },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "eau",
    enabled: !!champSouterraineId && perBUIds.length > 0,
  });

  const { groupData: buReseauData, isLoading: buReseauLoading } = useGetPerGroupAnalytics({
    baseFilters: { ...baseWaterGroupFilters, champ_metier: champReseauId },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "eau",
    enabled: !!champReseauId && perBUIds.length > 0,
  });

  const buKpiLoading = buSurfaceLoading || buSouterraineLoading || buReseauLoading;

  const buWaterTotals = useMemo(() => {
    // Initialize all BUs with 0 so every BU appears even without data
    const totals: Record<number, { id: number; name: string; total: number }> = {};
    for (const b of perBUIds) {
      totals[b] = { id: b, name: buNamesMap[b] ?? String(b), total: 0 };
    }
    for (const entry of [...buSurfaceData, ...buSouterraineData, ...buReseauData]) {
      if (!totals[entry.groupId]) {
        totals[entry.groupId] = { id: entry.groupId, name: entry.groupName, total: 0 };
      }
      totals[entry.groupId].total += entry.total;
    }
    return Object.values(totals).sort((a, b) => b.total - a.total);
  }, [perBUIds, buNamesMap, buSurfaceData, buSouterraineData, buReseauData]);

  // Fetch data for each water source type (for pie chart)
  const { data: surfaceData } = useGetEauAnalytics({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    dimension_type: dimensionType,
    champ_metier: champSurfaceId,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  const { data: souterraineData } = useGetEauAnalytics({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    dimension_type: dimensionType,
    champ_metier: champSouterraineId,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  const { data: reseauData } = useGetEauAnalytics({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    dimension_type: dimensionType,
    champ_metier: champReseauId,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  const { data: traiteeData, isLoading: traiteeLoading } = useGetEauAnalytics({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    dimension_type: dimensionType,
    champ_metier: champTraiteeId,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  // Ratios data (only fetched when ratios tab is active)
  const ratiosFilters = useMemo(() => ({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    site_ids: selectedSites.length > 0 ? selectedSites.map(Number) : undefined,
    bu_ids: selectedBUs.length > 0 ? selectedBUs.map(Number) : undefined,
    activite_ids: selectedActivites.length > 0 ? selectedActivites.map(Number) : undefined,
    filiere_ids: selectedFilieres.length > 0 ? selectedFilieres.map(Number) : undefined,
    region_ids: selectedRegions.length > 0 ? selectedRegions.map(Number) : undefined,
  }), [effectivePeriods, selectedSites, selectedBUs, selectedActivites, selectedFilieres, selectedRegions]);

  const {
    ratioFamille,
    ratioProduit,
    evoConsoEau,
    consoSplitSite,
    isLoading: ratiosLoading,
    hasError: ratiosError,
  } = useGetEauRatios(ratiosFilters, activeTab === "ratios");

  // Water KPIs from /core/analytics/eau/ - shows sum of all champs metier
  const {
    data: eauKpisData,
    isLoading: kpisLoading,
    error,
  } = useGetEauKpis({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    dimension_type: dimensionType,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  // Deduplicate summary items by name (backend can produce duplicates when
  // both "produit" and "variete" dimensions share the same display name,
  // e.g. GRENADIER appears as both product and variete with EAU_PROD_ prefix).
  const deduplicatedSummary = useMemo(() => {
    if (!eauKpisData?.summary) return [];
    const byName = new Map<string, typeof eauKpisData.summary[0]>();
    for (const item of eauKpisData.summary) {
      const existing = byName.get(item.name);
      if (existing) {
        // Merge totals, keep the first code encountered
        byName.set(item.name, { ...existing, total: existing.total + item.total });
      } else {
        byName.set(item.name, { ...item });
      }
    }
    return Array.from(byName.values());
  }, [eauKpisData]);

  // Calculate total water consumption as sum of selected BUs
  const totalWaterKpi = useMemo(() => {
    return buWaterTotals.reduce((sum, bu) => sum + bu.total, 0);
  }, [buWaterTotals]);

  // --- Cascading filter logic ---
  // Filter sites based on all selected organizational entities
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      if (selectedBUs.length > 0 && !selectedBUs.includes(site.bu)) return false;
      if (selectedActivites.length > 0 && !selectedActivites.includes(site.activite)) return false;
      if (selectedFilieres.length > 0 && !selectedFilieres.includes(site.filiere)) return false;
      if (selectedRegions.length > 0 && !selectedRegions.includes(site.region)) return false;
      return true;
    });
  }, [sites, selectedBUs, selectedActivites, selectedFilieres, selectedRegions]);

  // Get unique entity IDs from filtered sites for cascading dropdowns
  const availableBUIds = useMemo(() => {
    return new Set(sites.map((s) => s.bu));
  }, [sites]);

  const availableActiviteIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    return new Set(sitesToCheck.map((s) => s.activite));
  }, [sites, selectedBUs]);

  const availableFiliereIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    if (selectedActivites.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedActivites.includes(s.activite));
    }
    return new Set(sitesToCheck.map((s) => s.filiere));
  }, [sites, selectedBUs, selectedActivites]);

  const availableRegionIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    if (selectedActivites.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedActivites.includes(s.activite));
    }
    if (selectedFilieres.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedFilieres.includes(s.filiere));
    }
    return new Set(sitesToCheck.map((s) => s.region));
  }, [sites, selectedBUs, selectedActivites, selectedFilieres]);

  // --- Dropdown items (filtered based on hierarchy) ---
  const buItems = useMemo(
    () => bus.filter((b) => availableBUIds.has(b.id)).map((b) => ({ value: b.id, label: `${b.code} - ${b.name}` })),
    [bus, availableBUIds]
  );

  const activiteItems = useMemo(
    () => activites.filter((a) => availableActiviteIds.has(a.id)).map((a) => ({ value: a.id, label: `${a.code} - ${a.name}` })),
    [activites, availableActiviteIds]
  );

  const filiereItems = useMemo(
    () => filieres.filter((f) => availableFiliereIds.has(f.id)).map((f) => ({ value: f.id, label: `${f.code} - ${f.name}` })),
    [filieres, availableFiliereIds]
  );

  const regionItems = useMemo(
    () => regions.filter((r) => availableRegionIds.has(r.id)).map((r) => ({ value: r.id, label: `${r.code} - ${r.name}` })),
    [regions, availableRegionIds]
  );

  const siteItems = useMemo(
    () => filteredSites.map((s) => ({ value: s.id, label: s.name })),
    [filteredSites]
  );

  // Helper function to group monthly time series data by campaigns
  const groupByCampaign = <T extends Record<string, any>>(timeSeries: T[]) => {
    const campaignGroups = new Map<string, T[]>();

    timeSeries.forEach((ts) => {
      const month = ts.month; // Format: "YYYY-MM"
      const [year, monthNum] = month.split('-').map(Number);

      // Determine which campaign this month belongs to
      // Campaign runs from Oct (10) to Sep (09)
      const campaign = monthNum >= 10
        ? `${year % 100}/${(year + 1) % 100}`
        : `${(year - 1) % 100}/${year % 100}`;

      if (!campaignGroups.has(campaign)) {
        campaignGroups.set(campaign, []);
      }
      campaignGroups.get(campaign)!.push(ts);
    });

    return campaignGroups;
  };

  // ============================================
  // CHART DATA: Water Sources Distribution Pie (by champs metier)
  // ============================================
  const waterSourcesPieData = useMemo(() => {
    const sources = [
      { label: "Eau Surface", total: surfaceData?.summary?.total || 0, color: "#3b82f6" },
      { label: "Eau Souterraine", total: souterraineData?.summary?.total || 0, color: "#8b5cf6" },
      { label: "Eau Réseau", total: reseauData?.summary?.total || 0, color: "#10b981" },
    ];

    const totalSum = sources.reduce((sum, s) => sum + s.total, 0);
    if (totalSum === 0) return null;

    return {
      labels: sources.map((s) => s.label),
      datasets: [
        {
          data: sources.map((s) => s.total),
          backgroundColor: sources.map((s) => s.color),
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    };
  }, [surfaceData, souterraineData, reseauData]);

  // ============================================
  // CHART DATA: Product/Family Contribution Pie
  // ============================================
  const productFamilyPieData = useMemo(() => {
    if (!deduplicatedSummary.length) return null;
    return {
      labels: deduplicatedSummary.map((item) => item.name),
      datasets: [
        {
          data: deduplicatedSummary.map((item) => item.total),
          backgroundColor: deduplicatedSummary.map((item, i) =>
            getGroupColor(item.name, FAMILLE_COLORS, i)
          ),
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    };
  }, [deduplicatedSummary]);

  // ============================================
  // CHART DATA: Evolution Conso Eau (simple bar - total per month/campaign)
  // ============================================
  const evolutionBarData = useMemo(() => {
    if (!eauKpisData?.time_series || eauKpisData.time_series.length === 0) return null;

    // Get all codes from deduplicated summary
    const codes = deduplicatedSummary.map(item => item.code);

    if (filterType === "campaigns") {
      // Group by campaign and sum values
      const campaignGroups = groupByCampaign(eauKpisData.time_series);
      const campaignTotals: { campaign: string; total: number }[] = [];

      campaignGroups.forEach((monthsInCampaign, campaign) => {
        const total = monthsInCampaign.reduce((sum, ts) => {
          return sum + codes.reduce((codeSum, code) => codeSum + ((ts[code] as number) || 0), 0);
        }, 0);
        campaignTotals.push({ campaign, total });
      });

      // Sort by campaign chronologically
      campaignTotals.sort((a, b) => a.campaign.localeCompare(b.campaign));

      return {
        labels: campaignTotals.map((c) => c.campaign),
        datasets: [
          {
            label: "Consommation Eau",
            data: campaignTotals.map((c) => c.total),
            backgroundColor: "#37daafCC",
            borderRadius: 6,
            borderSkipped: false as const,
          },
        ],
      };
    } else {
      // Calculate total per month (sum all codes)
      const monthlyTotals = eauKpisData.time_series.map((ts) => {
        const total = codes.reduce((sum, code) => sum + ((ts[code] as number) || 0), 0);
        return { month: ts.month, total };
      });

      return {
        labels: monthlyTotals.map((m) => m.month),
        datasets: [
          {
            label: "Consommation Eau",
            data: monthlyTotals.map((m) => m.total),
            backgroundColor: "#37daafCC",
            borderRadius: 6,
            borderSkipped: false as const,
          },
        ],
      };
    }
  }, [eauKpisData, deduplicatedSummary, filterType]);

  // ============================================
  // CHART DATA: Evolution par Famille/Produit (stacked bar - month/campaign)
  // ============================================
  const evolutionStackedBarData = useMemo(() => {
    if (!eauKpisData?.time_series || eauKpisData.time_series.length === 0) return null;
    if (!deduplicatedSummary.length) return null;

    // Use deduplicated summary to get codes and names
    const codes = deduplicatedSummary.map(item => item.code);
    const codeNames = new Map(deduplicatedSummary.map(item => [item.code, item.name]));

    if (filterType === "campaigns") {
      // Group by campaign and sum values per code
      const campaignGroups = groupByCampaign(eauKpisData.time_series);
      const campaigns: string[] = [];
      const campaignData = new Map<string, number[]>();

      // Initialize data structure
      codes.forEach(code => campaignData.set(code, []));

      // Process each campaign
      campaignGroups.forEach((monthsInCampaign, campaign) => {
        campaigns.push(campaign);

        codes.forEach(code => {
          const total = monthsInCampaign.reduce((sum, ts) => sum + ((ts[code] as number) || 0), 0);
          campaignData.get(code)!.push(total);
        });
      });

      // Sort campaigns chronologically
      const sortedIndices = campaigns
        .map((c, i) => ({ campaign: c, index: i }))
        .sort((a, b) => a.campaign.localeCompare(b.campaign))
        .map(x => x.index);

      const sortedCampaigns = sortedIndices.map(i => campaigns[i]);
      const sortedData = new Map<string, number[]>();
      codes.forEach(code => {
        sortedData.set(code, sortedIndices.map(i => campaignData.get(code)![i]));
      });

      return {
        labels: sortedCampaigns,
        datasets: codes.map((code, i) => ({
          label: codeNames.get(code) || code,
          data: sortedData.get(code)!,
          backgroundColor: getGroupColor(codeNames.get(code) || code, FAMILLE_COLORS, i) + "CC",
          borderRadius: i === codes.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        })),
      };
    } else {
      // Monthly data
      return {
        labels: eauKpisData.time_series.map((ts) => ts.month),
        datasets: codes.map((code, i) => ({
          label: codeNames.get(code) || code,
          data: eauKpisData.time_series.map((ts) => (ts[code] as number) || 0),
          backgroundColor: getGroupColor(codeNames.get(code) || code, FAMILLE_COLORS, i) + "CC",
          borderRadius: i === codes.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        })),
      };
    }
  }, [eauKpisData, deduplicatedSummary, filterType]);

  // ============================================
  // Common chart options
  // ============================================
  const stackedBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            usePointStyle: true,
            pointStyle: "circle" as const,
            font: { size: 11, family: "Arial Black" },
            color: "#475569",
            padding: 16,
          },
          onClick: legendIsolateClickDataset,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          titleFont: { size: 13, weight: "bold" as const },
          bodyFont: { size: 12 },
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${formatLargeNumber(ctx.parsed.y ?? 0)}`,
          },
        },
        datalabels: {
          display: false, // Hide to avoid clutter with many items
        },
      },
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: "#f1f5f9" },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value: string | number) => formatLargeNumber(Number(value)),
          },
        },
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
      },
    }),
    []
  );

  // Simple bar options for Evolution Conso Eau chart
  const simpleBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          titleFont: { size: 13, weight: "bold" as const },
          bodyFont: { size: 12 },
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) =>
              `Consommation: ${formatLargeNumber(ctx.parsed.y ?? 0)} m³`,
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "#f1f5f9" },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value: string | number) => formatLargeNumber(Number(value)),
          },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
      },
    }),
    []
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "55%",
      plugins: {
        legend: {
          position: "right" as const,
          labels: {
            padding: 12,
            usePointStyle: true,
            pointStyle: "circle" as const,
            font: { size: 10, family: "Arial Black" },
            color: "#475569",
          },
          onClick: legendIsolateClickPie,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed;
              const allData = ctx.dataset.data as number[];
              const pieTotal = allData.reduce((s: number, v: number) => s + v, 0);
              const pct = pieTotal > 0 ? ((val / pieTotal) * 100).toFixed(1) : "0";
              return `${formatLargeNumber(val)} | ${pct}%`;
            },
          },
        },
        datalabels: {
          display: false, // Hide labels on pie to avoid clutter
        },
      },
    }),
    []
  );

  // ============================================
  // Horizontal bar options for ratio charts
  // ============================================
  const horizontalBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => `${formatLargeNumber(ctx.parsed.x ?? 0)} m³/ha`,
          },
        },
        datalabels: {
          display: true,
          anchor: "end" as const,
          align: "right" as const,
          formatter: (value: number) => formatLargeNumber(value),
          color: "#334155",
          font: { size: 11, weight: "bold" as const },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: "#f1f5f9" },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value: string | number) => formatLargeNumber(Number(value)),
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: "#475569", font: { size: 11, weight: "bold" as const } },
        },
      },
    }),
    []
  );

  // ============================================
  // RATIO CHART DATA
  // ============================================
  const ratioFamilleChartData = useMemo(() => {
    if (!ratioFamille.length) return null;
    return {
      labels: ratioFamille.map((r) => r.name),
      datasets: [{
        label: "m³",
        data: ratioFamille.map((r) => Math.round(r.ratio)),
        backgroundColor: ratioFamille.map((r, i) =>
          getGroupColor(r.name, FAMILLE_COLORS, i) + "CC"
        ),
        borderRadius: 4,
        borderSkipped: false as const,
      }],
    };
  }, [ratioFamille]);

  const ratioProduitChartData = useMemo(() => {
    if (!ratioProduit.length) return null;
    return {
      labels: ratioProduit.map((r) => r.name),
      datasets: [{
        label: "m³",
        data: ratioProduit.map((r) => Math.round(r.ratio)),
        backgroundColor: ratioProduit.map((r, i) =>
          getGroupColor(r.name, FAMILLE_COLORS, i) + "CC"
        ),
        borderRadius: 4,
        borderSkipped: false as const,
      }],
    };
  }, [ratioProduit]);

  const evoConsoEauChartData = useMemo(() => {
    if (!evoConsoEau.length) return null;
    return {
      labels: evoConsoEau.map((r) => r.month),
      datasets: [{
        label: "m³/ha",
        data: evoConsoEau.map((r) => Math.round(r.ratio)),
        backgroundColor: "#37daafCC",
        borderRadius: 6,
        borderSkipped: false as const,
      }],
    };
  }, [evoConsoEau]);

  const consoSplitSiteChartData = useMemo(() => {
    if (!consoSplitSite.length) return null;
    return {
      labels: consoSplitSite.map((r) => r.siteName),
      datasets: [{
        label: "m³/ha",
        data: consoSplitSite.map((r) => Math.round(r.ratio)),
        backgroundColor: "#37daafCC",
        borderRadius: 4,
        borderSkipped: false as const,
      }],
    };
  }, [consoSplitSite]);

  const hasData = deduplicatedSummary.length > 0;
  const hasRatiosData = ratioFamille.length > 0 || ratioProduit.length > 0;

  return (
    <div className="water-page">
      <Topbar
        title={pageTitle}
        userName={user.first_name || user.username}
        onLogout={handleLogout}
        user={user}
        sites={sites}
      />

      <div className="water-content">
        {/* Header */}
        <div className="water-header">
          <div className="water-header-left">
            <div className="water-icon-wrapper" style={{ background: `${ACCENT}20` }}>
              <Droplets size={28} style={{ color: ACCENT }} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="water-title">Gestion de l'Eau</h1>
              <p className="water-subtitle">Analyse et visualisation de la consommation d'eau</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="water-card water-filters">
          <div className="water-filters-header">
            <Filter size={18} style={{ color: ACCENT }} />
            <span>Filtres et Paramètres</span>
          </div>
          <div className="water-filters-grid">
            <div className="water-filter-item">
              <label className="water-label">Type de période</label>
              <div className="water-period-toggle">
                {(["months", "campaigns"] as const).map((p) => (
                  <button
                    key={p}
                    className={`water-toggle-btn${filterType === p ? " active" : ""}`}
                    onClick={() => setFilterType(p)}
                  >
                    {p === "campaigns" ? "Par Campagne" : "Par Mois"}
                  </button>
                ))}
              </div>
            </div>

            {filterType === "months" ? (
              <>
                <div className="water-filter-item">
                  <label className="water-label">
                    <Calendar size={14} style={{ color: ACCENT }} />
                    Période de début
                  </label>
                  <select
                    value={fromPeriod}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setFromPeriod(newFrom);
                      // Auto-update end period to match if it's before the new start
                      if (toPeriod < newFrom) {
                        setToPeriod(newFrom);
                      }
                    }}
                    className="water-select"
                  >
                    {availableMonths.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="water-filter-item">
                  <label className="water-label">
                    <Calendar size={14} style={{ color: ACCENT }} />
                    Période de fin
                  </label>
                  <select
                    value={toPeriod}
                    onChange={(e) => setToPeriod(e.target.value)}
                    className="water-select"
                  >
                    {availableMonths.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="water-filter-item">
                  <label className="water-label">
                    <Calendar size={14} style={{ color: ACCENT }} />
                    Campagne de début
                  </label>
                  <select
                    value={fromCampaign}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setFromCampaign(newFrom);
                      // Auto-update end campaign to match if it's before the new start
                      const fromIndex = availableCampaigns.indexOf(newFrom);
                      const toIndex = availableCampaigns.indexOf(toCampaign);
                      if (toIndex < fromIndex) {
                        setToCampaign(newFrom);
                      }
                    }}
                    className="water-select"
                  >
                    {availableCampaigns.map((camp) => (
                      <option key={camp} value={camp}>
                        {camp}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="water-filter-item">
                  <label className="water-label">
                    <Calendar size={14} style={{ color: ACCENT }} />
                    Campagne de fin
                  </label>
                  <select
                    value={toCampaign}
                    onChange={(e) => setToCampaign(e.target.value)}
                    className="water-select"
                  >
                    {availableCampaigns.map((camp) => (
                      <option key={camp} value={camp}>
                        {camp}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="water-filter-item">
              <label className="water-label">Type de dimension</label>
              <select
                value={dimensionType}
                onChange={(e) => setDimensionType(e.target.value as "produit" | "famille")}
                className="water-select"
              >
                <option value="produit">Par Produit</option>
                <option value="famille">Par Famille</option>
              </select>
            </div>
            <div className="water-filter-item">
              <label className="water-label">Site</label>
              <MultiSearchableCombobox
                items={siteItems}
                values={selectedSites}
                onChange={setSelectedSites}
                placeholder="Tous les sites"
                accentColor={ACCENT}
              />
            </div>
            <div className="water-filter-item">
              <label className="water-label">Business Unit</label>
              <MultiSearchableCombobox
                items={buItems}
                values={selectedBUs}
                onChange={setSelectedBUs}
                placeholder="Toutes les BU"
                accentColor={ACCENT}
              />
            </div>
            <div className="water-filter-item">
              <label className="water-label">Activité</label>
              <MultiSearchableCombobox
                items={activiteItems}
                values={selectedActivites}
                onChange={setSelectedActivites}
                placeholder="Toutes les activités"
                accentColor={ACCENT}
              />
            </div>
            <div className="water-filter-item">
              <label className="water-label">Filière</label>
              <MultiSearchableCombobox
                items={filiereItems}
                values={selectedFilieres}
                onChange={setSelectedFilieres}
                placeholder="Toutes les filières"
                accentColor={ACCENT}
              />
            </div>
            <div className="water-filter-item">
              <label className="water-label">Région</label>
              <MultiSearchableCombobox
                items={regionItems}
                values={selectedRegions}
                onChange={setSelectedRegions}
                placeholder="Toutes les régions"
                accentColor={ACCENT}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="water-tabs">
          <button
            className={`water-tab ${activeTab === "kpis" ? "water-tab--active" : ""}`}
            onClick={() => setActiveTab("kpis")}
            style={{
              borderColor: activeTab === "kpis" ? ACCENT : "transparent",
              color: activeTab === "kpis" ? ACCENT : "#64748b",
            }}
          >
            <Droplets size={16} />
            Main KPIs
          </button>
          <button
            className={`water-tab ${activeTab === "ratios" ? "water-tab--active" : ""}`}
            onClick={() => setActiveTab("ratios")}
            style={{
              borderColor: activeTab === "ratios" ? ACCENT : "transparent",
              color: activeTab === "ratios" ? ACCENT : "#64748b",
            }}
          >
            <Ratio size={16} />
            Ratios
          </button>
        </div>

        {/* ═══════════════ Main KPIs Tab ═══════════════ */}
        {activeTab === "kpis" && (
        <>
        {/* Main KPIs - Water Consumption (matching Def_Eau Main KPIs) */}
        <div className="water-kpi-grid">
          {/* Total Water Consumption */}
          <div
            className="water-kpi"
            style={{
              background: `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
            }}
          >
            <div className="water-kpi-content">
              <div>
                <p className="water-kpi-label">Total Consomm{"\u00e9"}</p>
                <p className="water-kpi-value">
                  {kpisLoading ? "..." : formatLargeNumber(totalWaterKpi)}
                </p>
                <p className="water-kpi-unit">m{"\u00B3"}</p>
              </div>
              <div className="water-kpi-icon">
                <Droplets size={26} />
              </div>
            </div>
          </div>

          {/* Per-BU water consumption KPI cards */}
          {buWaterTotals.filter((bu) => bu.total > 0).map((bu, index) => {
            const buColors = [
              "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
              "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
              "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
              "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
              "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
              "linear-gradient(135deg, #84cc16 0%, #65a30d 100%)",
            ];
            return (
              <div
                key={bu.id}
                className="water-kpi"
                style={{ background: buColors[index % buColors.length] }}
              >
                <div className="water-kpi-content">
                  <div>
                    <p className="water-kpi-label">{bu.name}</p>
                    <p className="water-kpi-value">
                      {buKpiLoading ? "..." : formatLargeNumber(bu.total)}
                    </p>
                    <p className="water-kpi-unit">m{"\u00B3"}</p>
                  </div>
                  <div className="water-kpi-icon">
                    <TrendingUp size={26} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Loading */}
        {kpisLoading && (
          <div className="water-card water-state-card">
            <div className="water-spinner" style={{ borderTopColor: ACCENT }} />
            <p className="water-state-text">Chargement des données...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="water-error">
            <h3>Erreur de chargement</h3>
            <p>Impossible de charger les données. Veuillez réessayer.</p>
          </div>
        )}

        {/* No data */}
        {!kpisLoading && !error && !hasData && (
          <div className="water-card water-state-card">
            <Droplets size={56} style={{ color: "#cbd5e1" }} />
            <h3 className="water-state-title">Aucune donnée disponible</h3>
            <p className="water-state-text">Aucune donnée trouvée pour les filtres sélectionnés.</p>
          </div>
        )}

        {/* Charts */}
        {!kpisLoading && !error && hasData && (
          <>
            {/* Row 1: Pie charts - Water Sources and Product/Family */}
            <div className="water-charts-grid water-charts-grid--two">
              {/* Pie chart showing water sources distribution */}
              {waterSourcesPieData && (
                <div className="water-card water-chart-card">
                  <h3 className="water-chart-title">
                    <PieChart size={18} style={{ color: ACCENT }} />
                    Répartition par Source d'Eau (m³)
                  </h3>
                  <div className="water-chart-container water-chart-container--pie">
                    <Doughnut data={waterSourcesPieData} options={pieOptions} />
                  </div>
                </div>
              )}

              {/* Pie chart showing contribution of each product/family */}
              {productFamilyPieData && (
                <div className="water-card water-chart-card">
                  <h3 className="water-chart-title">
                    <PieChart size={18} style={{ color: ACCENT }} />
                    Répartition par {dimensionType === "produit" ? "Produit" : "Famille"} (m³)
                  </h3>
                  <div className="water-chart-container water-chart-container--pie">
                    <Doughnut data={productFamilyPieData} options={pieOptions} />
                  </div>
                </div>
              )}
            </div>

            {/* Row 2: Evolution charts side by side like in Def_Eau */}
            <div className="water-charts-grid water-charts-grid--two">
              {/* Evolution Conso Eau - Simple bar chart (total per month) */}
              {evolutionBarData && (
                <div className="water-card water-chart-card">
                  <h3 className="water-chart-title">
                    <BarChart3 size={18} style={{ color: ACCENT }} />
                    Évolution Conso Eau (m³)
                  </h3>
                  <div className="water-chart-container">
                    <Bar data={evolutionBarData} options={simpleBarOptions} />
                  </div>
                </div>
              )}

              {/* Evolution par Famille/Produit - Stacked bar chart */}
              {evolutionStackedBarData && (
                <div className="water-card water-chart-card">
                  <h3 className="water-chart-title">
                    <BarChart3 size={18} style={{ color: ACCENT }} />
                    Évolution par {dimensionType === "produit" ? "Produit" : "Famille"} (m³)
                  </h3>
                  <div className="water-chart-container">
                    <Bar data={evolutionStackedBarData} options={stackedBarOptions} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        </>
        )}

        {/* ═══════════════ Ratios Tab ═══════════════ */}
        {activeTab === "ratios" && (
          <>
            {/* Loading */}
            {ratiosLoading && (
              <div className="water-card water-state-card">
                <div className="water-spinner" style={{ borderTopColor: ACCENT }} />
                <p className="water-state-text">Chargement des ratios...</p>
              </div>
            )}

            {/* Error */}
            {ratiosError && (
              <div className="water-error">
                <h3>Erreur de chargement</h3>
                <p>Impossible de charger les ratios. Veuillez réessayer.</p>
              </div>
            )}

            {/* No data */}
            {!ratiosLoading && !ratiosError && !hasRatiosData && (
              <div className="water-card water-state-card">
                <Ratio size={56} style={{ color: "#cbd5e1" }} />
                <h3 className="water-state-title">Aucune donnée disponible</h3>
                <p className="water-state-text">Aucune donnée de ratio trouvée pour les filtres sélectionnés.</p>
              </div>
            )}

            {/* Ratio Charts */}
            {!ratiosLoading && !ratiosError && hasRatiosData && (
              <>
                {/* Row 1: Ratio Famille + Ratio Produit */}
                <div className="water-charts-grid water-charts-grid--two">
                  {ratioFamilleChartData && (
                    <div className="water-card water-chart-card">
                      <h3 className="water-chart-title">
                        <BarChart3 size={18} style={{ color: "#10b981" }} />
                        Eau par Famille (m³)
                      </h3>
                      <div className="water-chart-container water-chart-container--tall">
                        <Bar data={ratioFamilleChartData} options={horizontalBarOptions} />
                      </div>
                    </div>
                  )}
                  {ratioProduitChartData && (
                    <div className="water-card water-chart-card">
                      <h3 className="water-chart-title">
                        <BarChart3 size={18} style={{ color: "#f59e0b" }} />
                        Eau par Produit (m³)
                      </h3>
                      <div className="water-chart-container water-chart-container--extra-tall">
                        <Bar data={ratioProduitChartData} options={horizontalBarOptions} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Row 2: Evo Conso Eau + Conso Split Site */}
                <div className="water-charts-grid water-charts-grid--two">
                  {evoConsoEauChartData && (
                    <div className="water-card water-chart-card">
                      <h3 className="water-chart-title">
                        <BarChart3 size={18} style={{ color: "#37daaf" }} />
                        Evo Conso Eau (m³/ha)
                      </h3>
                      <div className="water-chart-container">
                        <Bar data={evoConsoEauChartData} options={simpleBarOptions} />
                      </div>
                    </div>
                  )}
                  {consoSplitSiteChartData && (
                    <div className="water-card water-chart-card">
                      <h3 className="water-chart-title">
                        <BarChart3 size={18} style={{ color: "#37daaf" }} />
                        Conso Split Site (m³/ha)
                      </h3>
                      <div className="water-chart-container water-chart-container--tall">
                        <Bar data={consoSplitSiteChartData} options={horizontalBarOptions} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
        {/* ============================================ */}
        {/* Evolution des Rejets liquides traités (m³)  */}
        {/* ============================================ */}
        {champTraiteeId && (
          <div className="water-card water-chart-card" style={{ marginTop: 20, padding: "20px 24px" }}>
            <h3 className="water-chart-title">
              <BarChart3 size={18} style={{ color: "#0ea5e9" }} />
              Evolution des Rejets liquides trait&eacute;s (m&sup3;)
            </h3>

            {traiteeLoading && (
              <div className="water-state-card" style={{ padding: "24px 0", textAlign: "center" }}>
                <div className="water-spinner" />
                <p className="water-state-text">Chargement...</p>
              </div>
            )}

            {!traiteeLoading && (() => {
              const ts = traiteeData?.time_series;
              if (!ts?.length) return (
                <p style={{ textAlign: "center", color: "#94a3b8", padding: "24px 0", fontSize: 13 }}>
                  Aucune donn&eacute;e de rejet trait&eacute; pour la p&eacute;riode s&eacute;lectionn&eacute;e.
                </p>
              );

              let labels: string[];
              let values: number[];

              if (filterType === "campaigns") {
                const groups = groupByCampaign(ts.map(t => ({ month: t.month, valeur: t.valeur })));
                const pairs: { l: string; v: number }[] = [];
                groups.forEach((months, campaign) => {
                  pairs.push({ l: campaign, v: months.reduce((s, m) => s + ((m as any).valeur || 0), 0) });
                });
                pairs.sort((a, b) => a.l.localeCompare(b.l));
                labels = pairs.map(p => p.l);
                values = pairs.map(p => p.v);
              } else {
                labels = ts.map(t => t.month);
                values = ts.map(t => t.valeur);
              }

              const chartData = {
                labels,
                datasets: [{
                  label: "Rejets traités (m³)",
                  data: values,
                  backgroundColor: "#0ea5e9CC",
                  borderRadius: 6,
                  borderSkipped: false as const,
                }],
              };

              return (
                <div className="water-chart-container water-chart-container--tall">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: "bottom" as const,
                          labels: { usePointStyle: true, pointStyle: "circle" as const, font: { size: 11 }, color: "#475569", padding: 16 },
                          onClick: legendIsolateClickDataset,
                        },
                        tooltip: {
                          backgroundColor: "#1e293b",
                          titleFont: { size: 13, weight: "bold" as const },
                          bodyFont: { size: 12 },
                          padding: 12,
                          cornerRadius: 8,
                          callbacks: {
                            label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("fr-FR")} m³`,
                          },
                        },
                        datalabels: {
                          display: true,
                          anchor: "end" as const,
                          align: "end" as const,
                          formatter: (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0),
                          color: "#334155",
                          font: { size: 10, weight: "bold" as const },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { color: "#f1f5f9" },
                          ticks: {
                            color: "#94a3b8",
                            font: { size: 11 },
                            callback: (v: string | number) => {
                              const n = Number(v);
                              return n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
                            },
                          },
                          title: { display: true, text: "m³", color: "#94a3b8", font: { size: 11 } },
                        },
                        x: {
                          grid: { display: false },
                          ticks: { color: "#94a3b8", font: { size: 11 }, maxRotation: 45, minRotation: 0 },
                        },
                      },
                    }}
                  />
                </div>
              );
            })()}
          </div>
        )}

      </div>
    </div>
  );
};

export default WaterPage;
